import { useState } from 'react';
import classNames from 'classnames/bind';
import { useAppSelector } from '../../../../store/hooks';
import { selectUserInformation } from '../../../../store/reducers';
import type { ReducerAction } from '../../../../types/navigationIndex.types';
import { Button, InputField, RadioButton } from '../../../atoms';
import { Checkbox } from '../../../molecules';
import { ProgressStep, ProgressSteps } from '../../../organisms';
import { PRODUCTS } from './products.data';
import { STOCK } from './stock.data';
import style from './ProductStep.module.scss';

function doesNotInclude<T>(this: T[], value: T): boolean {
  return !this.includes(value);
}

/** Toggle an item in and out of an array (immutably) and return a new array with or without the specified items (or the same array if no changes were made). */
function toggleArrayItems<T>(arr: ReadonlyArray<T>, ...toggle: T[]): ReadonlyArray<T> {
  const extras = toggle.filter(doesNotInclude, arr);
  const filtered = arr.filter(doesNotInclude, toggle);

  return extras.length ? [...filtered, ...extras] : filtered.length !== arr.length ? filtered : arr;
}

const cx = classNames.bind(style);

const questionContainerClasses = cx('flex', 'flex-column', 'mb-12');
const questionLabelClasses = cx('mb-4');
const productStepContainerClasses = cx({ ProductStepContainer: true, StepsContainer: true });
const buttonContainerClasses = cx({ ButtonContainer: true });
const radioRowContainerClasses = cx('flex', 'flex-start', 'gap-2');
const radioColumnContainerClasses = cx('flex', 'flex-column', 'gap-2', 'flex-space-around', 'mt-4');

type ProductStepProps = {
  isOffline: boolean;
  isFirstAppointment: boolean;
  setCurrentPage: React.Dispatch<ReducerAction>;
  appointmentInfo: any;
  setAppointmentInfo: (value: any) => void;
};

export function ProductStep({
  isOffline,
  isFirstAppointment,
  setCurrentPage,
  appointmentInfo,
  setAppointmentInfo,
}: ProductStepProps) {
  const userInformation = useAppSelector(selectUserInformation);
  const existingPharmacyId = userInformation.patientPharmacyRelation.id;

  const [products, setProducts] = useState<ReadonlyArray<string>>([]);
  const [pharmacyId, setPharmacyId] = useState<number | undefined>(undefined);
  const [patientNotes, setPatientNotes] = useState<string>('');

  const existingPharmacyStock = existingPharmacyId && STOCK.find(({ id }) => id === existingPharmacyId);
  const existingPharmacyHasStock =
    !products.length || (existingPharmacyStock && products.every((p) => existingPharmacyStock.stock.includes(p)));

  const isFormComplete = existingPharmacyHasStock || typeof pharmacyId === 'number';

  const next = () => {
    if (!isFormComplete) {
      return;
    }
    setAppointmentInfo({
      ...appointmentInfo,
      pharmacyId,
      products,
      patientNotes,
    });
    setCurrentPage({
      type: '[UPDATE]',
      payload: { appointmentIndex: 4 },
    });
  };

  return (
    <div className={productStepContainerClasses}>
      <div className='max-w-[550px] mx-auto mb-4'>
        <ProgressSteps>
          {isOffline && isFirstAppointment ? <ProgressStep title='Standort' active /> : null}
          <ProgressStep title='Datum' active />
          <ProgressStep title='Uhrzeit' active />
          <ProgressStep title='Information' active />
          <ProgressStep title='Bestätigung' />
          <ProgressStep title='Zahlung' />
        </ProgressSteps>
      </div>
      <div className={questionContainerClasses}>
        <div className={questionLabelClasses}>
          Bitte geben Sie alle Produkte an, die Sie mit Ihrem Arzt besprechen möchten
        </div>
        {PRODUCTS.map(({ name, price }) => {
          return (
            <div className={radioRowContainerClasses} key={name}>
              <Checkbox
                checked={products.includes(name)}
                label={
                  <>
                    <span>{name}</span> <span className='text-xs'>({price} &euro;/g)</span>
                  </>
                }
                onClick={() => setProducts((existing) => toggleArrayItems(existing, name))}
              />
            </div>
          );
        })}
      </div>
      <div className={questionContainerClasses}>
        <div className={questionLabelClasses}>
          Gibt es noch andere Medikamente oder Beschwerden, die Sie besprechen möchten?
        </div>
        <InputField
          value={patientNotes}
          onChange={(newValue) => {
            setPatientNotes(newValue.substring(0, 1000)); // Limit to 1000 characters
          }}
          multiline={true}
          placeholder='Bitte angeben...'
        />
      </div>
      {products.length > 0 && !existingPharmacyHasStock ? (
        <div className={questionContainerClasses}>
          <div className={questionLabelClasses}>
            Wir können nicht garantieren, dass Ihre aktuelle Apotheke die ausgewählten Produkte vorrätig hat. Möchten
            Sie zu einer Apotheke wechseln, die diese vorrätig hat?
          </div>
          <div className={radioColumnContainerClasses}>
            {STOCK.filter(({ stock }) => {
              return products.every((p) => stock.includes(p));
            }).map(({ id, name }) => {
              return (
                <RadioButton
                  key={id}
                  id={`${name}_${id}`}
                  value={id}
                  checked={pharmacyId === id}
                  onChange={() => setPharmacyId(id)}
                  name='pharmacyId'
                >
                  {name}
                </RadioButton>
              );
            })}
            {existingPharmacyId ? (
              <RadioButton
                id={`${name}_existing`}
                value={existingPharmacyId}
                checked={pharmacyId === existingPharmacyId}
                onChange={() => setPharmacyId(existingPharmacyId)}
                name='pharmacyId'
              >
                Nicht ändern
              </RadioButton>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className={buttonContainerClasses}>
        <Button disabled={!isFormComplete} onClick={next}>
          Weiter
        </Button>
      </div>
    </div>
  );
}
